import { useEffect, useRef, useState } from "react";
import { mediaUrl } from "@/lib/api";
import { MAX_POLYGON_POINTS, PRINT_SHAPES, clipPathFor, polygonClosed } from "@/lib/customPrint";

const HANDLES = [
  { id: "nw", x: 0, y: 0, cursor: "nwse-resize" },
  { id: "ne", x: 100, y: 0, cursor: "nesw-resize" },
  { id: "sw", x: 0, y: 100, cursor: "nesw-resize" },
  { id: "se", x: 100, y: 100, cursor: "nwse-resize" },
];

function clampArea(area, locked) {
  let x = Math.max(0, Math.min(96, Number(area.x) || 0));
  let y = Math.max(0, Math.min(96, Number(area.y) || 0));
  let w = Math.max(6, Math.min(100, Number(area.w) || 20));
  let h = Math.max(6, Math.min(100, Number(area.h) || 20));
  if (locked) {
    const s = Math.min(w, h);
    w = s;
    h = s;
  }
  if (x + w > 100) x = Math.max(0, 100 - w);
  if (y + h > 100) y = Math.max(0, 100 - h);
  return { ...area, x: +x.toFixed(2), y: +y.toFixed(2), w: +w.toFixed(2), h: +h.toFixed(2) };
}

function bboxFromPoints(points) {
  if (!points.length) return { x: 18, y: 18, w: 64, h: 64 };
  const xs = points.map((p) => p.x);
  const ys = points.map((p) => p.y);
  const x = Math.min(...xs);
  const y = Math.min(...ys);
  return { x, y, w: Math.max(4, Math.max(...xs) - x), h: Math.max(4, Math.max(...ys) - y) };
}

function dist(a, b) {
  return Math.hypot((a.x || 0) - (b.x || 0), (a.y || 0) - (b.y || 0));
}

export default function PrintAreaEditor({ imageUrl, value, onChange }) {
  const wrap = useRef(null);
  const [drag, setDrag] = useState(null);
  const area = value || { shape: "rect", x: 18, y: 18, w: 64, h: 64, points: [], closed: false };
  const locked = area.shape === "square" || area.shape === "circle";
  const tracing = area.shape === "polygon";
  const points = area.points || [];
  const closed = tracing && polygonClosed(area);
  const canClose = tracing && !closed && points.length >= 3;

  const pctFromEvent = (e) => {
    const el = wrap.current;
    if (!el) return { x: 0, y: 0 };
    const r = el.getBoundingClientRect();
    const pt = e.touches ? e.touches[0] : e;
    return {
      x: Math.max(0, Math.min(100, ((pt.clientX - r.left) / r.width) * 100)),
      y: Math.max(0, Math.min(100, ((pt.clientY - r.top) / r.height) * 100)),
    };
  };

  const setPoints = (nextPoints, nextClosed = false) => {
    const box = bboxFromPoints(nextPoints);
    onChange({
      ...area,
      shape: "polygon",
      points: nextPoints,
      closed: Boolean(nextClosed) && nextPoints.length >= 3,
      ...box,
    });
  };

  const closeShape = () => {
    if (points.length >= 3) setPoints(points, true);
  };

  useEffect(() => {
    if (!drag) return undefined;
    const move = (e) => {
      e.preventDefault();
      const p = pctFromEvent(e);
      if (drag.mode === "point") {
        const next = points.map((pt, i) => (i === drag.index ? { x: +p.x.toFixed(2), y: +p.y.toFixed(2) } : pt));
        setPoints(next, closed);
        return;
      }
      if (drag.mode === "draw") {
        const x = Math.min(drag.sx, p.x);
        const y = Math.min(drag.sy, p.y);
        let w = Math.abs(p.x - drag.sx);
        let h = Math.abs(p.y - drag.sy);
        onChange(clampArea({ ...area, x, y, w, h }, locked));
        return;
      }
      if (drag.mode === "move") {
        onChange(clampArea({ ...area, x: p.x - drag.ox, y: p.y - drag.oy, w: area.w, h: area.h }, locked));
        return;
      }
      const next = { ...area };
      if (drag.id.includes("w")) {
        const right = area.x + area.w;
        next.x = Math.min(p.x, right - 6);
        next.w = right - next.x;
      }
      if (drag.id.includes("e")) next.w = Math.max(6, p.x - area.x);
      if (drag.id.includes("n")) {
        const bottom = area.y + area.h;
        next.y = Math.min(p.y, bottom - 6);
        next.h = bottom - next.y;
      }
      if (drag.id.includes("s")) next.h = Math.max(6, p.y - area.y);
      onChange(clampArea(next, locked));
    };
    const up = () => setDrag(null);
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    window.addEventListener("touchmove", move, { passive: false });
    window.addEventListener("touchend", up);
    return () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      window.removeEventListener("touchmove", move);
      window.removeEventListener("touchend", up);
    };
  }, [drag, area, locked, onChange, points, closed]);

  if (!imageUrl) {
    return <p className="text-sm text-slate-500" data-testid="pf-print-need-image">Upload a blank product image to draw the print area.</p>;
  }

  return (
    <div className="space-y-3" data-testid="pf-print-editor">
      <div className="flex flex-wrap gap-2">
        {PRINT_SHAPES.map((s) => (
          <button
            key={s.id}
            type="button"
            data-testid={s.testId}
            onClick={() => {
              if (s.id === "polygon") {
                onChange({ ...area, shape: "polygon", points: points.length ? points : [], closed: Boolean(area.closed) && points.length >= 3 });
                return;
              }
              onChange(clampArea({ ...area, shape: s.id, points: [], closed: false }, s.id === "square" || s.id === "circle"));
            }}
            className={`h-8 px-3 rounded-full text-xs font-semibold border ${area.shape === s.id ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200"}`}
          >
            {s.label}
          </button>
        ))}
      </div>
      {tracing ? (
        <div className="rounded-lg border border-sky-100 bg-sky-50 px-3 py-2 text-xs text-sky-900" data-testid="pf-print-trace-help">
          Drop up to {MAX_POLYGON_POINTS} points around the photo window. After 3 or more, click <strong>point 1</strong> (or click near it) to close the shape — it fills instantly. Example: 1 → 2 → 3 → 4 then back to 1.
          <div className="mt-1 font-semibold" data-testid={closed ? "pf-print-shape-closed" : "pf-print-shape-open"}>
            {closed
              ? `Shape closed · ${points.length} / ${MAX_POLYGON_POINTS} points`
              : `${points.length} / ${MAX_POLYGON_POINTS} points${canClose ? " · click point 1 to close" : " · need at least 3 points"}`}
          </div>
          <div className="mt-2 flex gap-2">
            <button
              type="button"
              className="h-7 px-2 rounded-full border border-sky-200 bg-white font-semibold"
              data-testid="pf-print-undo-point"
              onClick={() => {
                if (closed) {
                  setPoints(points, false);
                  return;
                }
                setPoints(points.slice(0, -1), false);
              }}
              disabled={!points.length}
            >
              {closed ? "Reopen shape" : "Undo point"}
            </button>
            <button type="button" className="h-7 px-2 rounded-full border border-sky-200 bg-white font-semibold" data-testid="pf-print-clear-points" onClick={() => setPoints([])}>Clear points</button>
          </div>
        </div>
      ) : null}
      <div
        ref={wrap}
        className="relative w-full max-w-lg mx-auto select-none touch-none overflow-hidden rounded-xl bg-slate-100 border border-slate-200"
        data-testid="pf-print-canvas"
        onPointerDown={(e) => {
          if (e.target.closest("[data-print-handle]") || e.target.closest("[data-print-box]") || e.target.closest("[data-print-point]")) return;
          const p = pctFromEvent(e);
          if (tracing) {
            if (closed) return;
            if (canClose && dist(p, points[0]) <= 3.5) {
              closeShape();
              return;
            }
            if (points.length >= MAX_POLYGON_POINTS) return;
            setPoints([...points, { x: +p.x.toFixed(2), y: +p.y.toFixed(2) }], false);
            return;
          }
          setDrag({ mode: "draw", sx: p.x, sy: p.y });
          onChange(clampArea({ ...area, x: p.x, y: p.y, w: 6, h: 6 }, locked));
        }}
      >
        <img src={mediaUrl(imageUrl)} alt="" className="block w-full h-auto pointer-events-none" draggable={false} />
        {tracing ? (
          <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 100 100" preserveAspectRatio="none">
            {points.length >= 2 ? (
              closed ? (
                <polygon
                  fill="rgba(14,165,233,0.22)"
                  stroke="#0284c7"
                  strokeWidth="0.6"
                  points={points.map((pt) => `${pt.x},${pt.y}`).join(" ")}
                />
              ) : (
                <polyline
                  fill="none"
                  stroke="#0284c7"
                  strokeWidth="0.6"
                  points={points.map((pt) => `${pt.x},${pt.y}`).join(" ")}
                />
              )
            ) : null}
            {canClose ? (
              <line
                x1={points[points.length - 1].x}
                y1={points[points.length - 1].y}
                x2={points[0].x}
                y2={points[0].y}
                stroke="#0284c7"
                strokeWidth="0.45"
                strokeDasharray="1.4 1.2"
              />
            ) : null}
          </svg>
        ) : (
          <div
            data-print-box
            className="absolute border-2 border-sky-500 bg-sky-400/15 cursor-move"
            style={{ left: `${area.x}%`, top: `${area.y}%`, width: `${area.w}%`, height: `${area.h}%`, clipPath: clipPathFor(area) }}
            onPointerDown={(e) => {
              e.stopPropagation();
              const p = pctFromEvent(e);
              setDrag({ mode: "move", ox: p.x - area.x, oy: p.y - area.y });
            }}
          >
            <span className="absolute left-1 top-1 text-[10px] font-bold text-white bg-sky-600/80 px-1.5 py-0.5 rounded">Print area</span>
            {HANDLES.map((h) => (
              <button
                key={h.id}
                type="button"
                data-print-handle={h.id}
                aria-label={`Resize ${h.id}`}
                className="absolute w-3.5 h-3.5 rounded-full bg-white border-2 border-sky-600 -translate-x-1/2 -translate-y-1/2"
                style={{ left: `${h.x}%`, top: `${h.y}%`, cursor: h.cursor }}
                onPointerDown={(e) => {
                  e.stopPropagation();
                  setDrag({ mode: "resize", id: h.id });
                }}
              />
            ))}
          </div>
        )}
        {tracing
          ? points.map((pt, i) => (
            <button
              key={i}
              type="button"
              data-print-point={i}
              data-testid={`pf-print-point-${i}`}
              title={i === 0 && canClose ? "Click to close the shape" : `Point ${i + 1}`}
              className={`absolute w-5 h-5 -translate-x-1/2 -translate-y-1/2 rounded-full text-white text-[10px] font-bold border-2 border-white shadow ${i === 0 && canClose ? "bg-emerald-600 ring-2 ring-emerald-300 w-6 h-6" : "bg-sky-600"}`}
              style={{ left: `${pt.x}%`, top: `${pt.y}%` }}
              onPointerDown={(e) => {
                e.stopPropagation();
                if (i === 0 && canClose) {
                  closeShape();
                  return;
                }
                setDrag({ mode: "point", index: i });
              }}
            >
              {i + 1}
            </button>
          ))
          : null}
      </div>
      <p className="text-xs text-slate-500">
        {tracing
          ? `You can place up to ${MAX_POLYGON_POINTS} points. The cutout is saved only after you close it by connecting back to point 1. Coordinates stay aligned on every screen size.`
          : "Click and drag on the photo to draw the area. Drag the box to move it, or use the corners to resize. Coordinates are saved as percentages so they stay correct on every screen size."}
      </p>
    </div>
  );
}
